import { DateTime } from "luxon";
import {
  CheckInHistoryItem,
  CompletedCheckInSummary,
  TrendCategory,
} from "./types";

export const MIN_VISIBLE_CONCERN_LEVEL = 6;
export const MIN_CRITICAL_CONCERN_LEVEL = 9;

export const trendCategoryLabel: Record<TrendCategory, string> = {
  "trending-up": "Trending up",
  "trending-down": "Trending down",
  stable: "Stable",
  oscillating: "Oscillating",
  "no-clear-pattern": "No clear pattern",
  "not-enough-data": "Not enough data",
};

/** Semantic color role per trend category; components translate this into their own hex/class. */
export type TrendCategorySemanticColor =
  | "success"
  | "error"
  | "purple"
  | "neutral";

export const trendCategorySemanticColor: Record<
  TrendCategory,
  TrendCategorySemanticColor
> = {
  "trending-up": "success",
  "trending-down": "error",
  stable: "neutral",
  oscillating: "purple",
  "no-clear-pattern": "neutral",
  "not-enough-data": "neutral",
};

export const formatTimeUntilMeeting = (meetingTime: DateTime): string => {
  const totalHours = meetingTime.diffNow("hours").hours;
  if (totalHours >= 24) {
    const days = Math.ceil(totalHours / 24);
    return `${days} day${days !== 1 ? "s" : ""}`;
  }
  const hours = Math.ceil(totalHours);
  return `${hours} hour${hours !== 1 ? "s" : ""}`;
};

export const formatCircularCountdown = (
  meetingTime: DateTime
): { value: string; label: string } => {
  const totalMinutes = meetingTime.diffNow("minutes").minutes;
  if (totalMinutes < 60) {
    return { value: String(Math.ceil(totalMinutes)), label: "min" };
  }
  const totalHours = totalMinutes / 60;
  if (totalHours < 24) {
    return { value: String(Math.ceil(totalHours)), label: "hrs" };
  }
  return { value: String(Math.ceil(totalHours / 24)), label: "days" };
};

const MAX_FILL_MINUTES = 24 * 60;

export const getMeetingFillPercent = (meetingTime: DateTime): number => {
  const totalMinutes = meetingTime.diffNow("minutes").minutes;
  if (totalMinutes <= 0) return 0;
  if (totalMinutes >= MAX_FILL_MINUTES) return 100;
  return (totalMinutes / MAX_FILL_MINUTES) * 100;
};

export const MAX_CHECK_INS = 4;

const toTime = (date: string) => new Date(date).getTime();

export const getLatestCheckIns = (
  summary: CompletedCheckInSummary,
  maxCheckIns: number = MAX_CHECK_INS
): CheckInHistoryItem[] => {
  // Without a completed date there's nothing to cut off, so keep all history.
  const completedTime = summary.checkInCompletedDate
    ? toTime(summary.checkInCompletedDate)
    : Infinity;

  return summary.checkInHistory
    .filter((item) => {
      const scheduledTime = toTime(item.scheduledDate);
      return !Number.isNaN(scheduledTime) && scheduledTime <= completedTime;
    })
    .sort((a, b) => toTime(a.scheduledDate) - toTime(b.scheduledDate))
    .slice(-maxCheckIns);
};
